import { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { PackComponents } from "./PackComponents";
import { useLang } from "@/lib/i18n";

export function ProductDetails({ p }) {
  const { data: all } = useProducts();
  const { lang } = useLang();
  const en = lang === "en";
  const comps = Array.isArray(p.components) ? p.components : [];
  const tabs = [
    p.benefits?.length ? { k: "benefits", l: en ? "Benefits" : "Bienfaits" } : null,
    p.usage ? { k: "usage", l: p.type === "pack" ? "Routine" : en ? "How to use" : "Utilisation" } : null,
    comps.length ? { k: "components", l: en ? "Contents" : "Composition" } : p.ingredients ? { k: "ingredients", l: en ? "Ingredients" : "Composition" } : null,
    Array.isArray(p.faq) && p.faq.length ? { k: "faq", l: en ? "FAQ" : "Questions" } : null,
  ].filter(Boolean);
  const [tab, setTab] = useState(tabs[0]?.k);
  if (!tabs.length) return null;
  const active = tabs.some((t) => t.k === tab) ? tab : tabs[0].k;

  return (
    <div data-testid="product-details" className="mt-10 border-t border-[var(--line)] pt-6">
      <div className="flex flex-wrap gap-x-8 gap-y-2">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`product-tab-${t.k}`} onClick={() => setTab(t.k)} className={`relative pb-3 text-xs uppercase tracking-[0.24em] transition-colors ${active === t.k ? "text-[var(--gold-soft)]" : "text-[var(--muted)] hover:text-[var(--parchment)]"}`}>
            {t.l}
            {active === t.k && <motion.span layoutId="pd-underline" className="absolute inset-x-0 -bottom-px h-px bg-[var(--gold)]" />}
          </button>
        ))}
      </div>
      <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="pt-6">
        {active === "benefits" && (
          <ul data-testid="product-benefits" className="grid gap-3 sm:grid-cols-2">
            {p.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm text-[var(--parchment)]/90"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" />{b}</li>
            ))}
          </ul>
        )}
        {active === "usage" && <p data-testid="product-usage" className="text-sm leading-relaxed text-[var(--parchment)]/90">{p.usage}</p>}
        {active === "components" && <PackComponents components={comps} all={all} />}
        {active === "ingredients" && (
          <div data-testid="product-ingredients">
            <p className="eyebrow mb-3">{en ? "Ingredients" : "Ingrédients"}</p>
            <p className="text-sm leading-relaxed text-[var(--parchment)]/85">{p.ingredients}</p>
          </div>
        )}
        {active === "faq" && (
          <dl data-testid="product-faq" className="space-y-5">
            {p.faq.map((f, i) => (
              <div key={f.q} data-testid={`product-faq-${i}`}>
                <dt className="text-sm font-semibold text-[var(--parchment)]">{f.q}</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-[var(--parchment)]/85">{f.a}</dd>
              </div>
            ))}
          </dl>
        )}
      </motion.div>
    </div>
  );
}
